import { useParams, Link } from "react-router-dom";
import { categories, comics } from "@/mocks/comics";
import { useState, useMemo } from "react";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"rating" | "views" | "update">("rating");

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || slug;

  const filtered = useMemo(() => {
    const list = slug === "all" ? [...comics] : comics.filter((c) => c.category === slug);
    if (sortBy === "rating") return list.sort((a, b) => b.rating - a.rating);
    if (sortBy === "views") return list.sort((a, b) => b.views - a.views);
    return list.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  }, [slug, sortBy]);

  return (
    <main>
      <section className="pt-24 pb-8 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1
            className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {slug === "all" ? "全部韩国漫画" : `${categoryName}韩国漫画`}
          </h1>
          <p className="text-foreground-500 text-sm mb-6">
            共 <strong>{filtered.length}</strong> 部{slug !== "all" ? categoryName : ""}韩国漫画推荐
          </p>

          <div className="flex gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === "all" ? "/" : `/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer no-underline transition-all ${
                  (cat.slug === "all" && slug === undefined) || cat.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200/70 border border-background-200/70"
                }`}
              >
                <i className={`${cat.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-400">排序：</span>
            {[
              { key: "rating" as const, label: "评分最高" },
              { key: "views" as const, label: "人气最高" },
              { key: "update" as const, label: "最近更新" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  sortBy === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 border border-background-200/70 hover:bg-background-200/50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-sm transition-all cursor-pointer no-underline"
              >
                <div className="relative overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${categoryName}韩国漫画`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-background-50 text-xs">
                    {comic.status}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <span className="text-background-50 text-xs font-medium flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 w-3.5 h-3.5 flex items-center justify-center"></i>
                      {comic.rating}
                    </span>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="font-semibold text-sm text-foreground-950 mb-1 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mb-2">{comic.author}</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
              <p className="text-foreground-500">该分类下暂无漫画推荐</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}